// Criacao de chave da API publica. Apos o create, vira a visao "copie agora":
// a chave em claro so existe nesta resposta — o banco guarda apenas o hash.
import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import Modal from '../Faturamento/Modal';
import BaseInput from '../General/FormsComp/Input/BaseInput';
import BaseSelect from '../General/FormsComp/Select/BaseSelect';
import BaseButton from '../General/FormsComp/Button/BaseButton';
import { UserContext } from '../../contexts/userProvider';
import apiUser from '../../apiUser';

const CriarChaveModal = ({ onClose, onCriada }) => {
  const { setAlert } = useContext(UserContext);
  const [usuarios, setUsuarios] = useState([]);
  const [escoposDisponiveis, setEscoposDisponiveis] = useState([]);
  const [nome, setNome] = useState('');
  const [usuarioId, setUsuarioId] = useState('');
  const [escopos, setEscopos] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [chaveCriada, setChaveCriada] = useState(null); // { id, chave, key_prefix }
  const [copiada, setCopiada] = useState(false);

  const aviso = useCallback((msg) => setAlert({
    visible: true, title: 'Atenção!', placeholder: msg, confirm: false, type: 'warning',
  }), [setAlert]);

  useEffect(() => {
    (async () => {
      try {
        const [respUsuarios, respEscopos] = await Promise.all([
          apiUser.get('/usuarios/all'),
          apiUser.get('/apikeys/escopos'),
        ]);
        if (respUsuarios.data.success) {
          setUsuarios(respUsuarios.data.dados.filter((u) => Number(u.ativo) === 1));
        }
        if (respEscopos.data.success) setEscoposDisponiveis(respEscopos.data.dados);
        if (!respUsuarios.data.success || !respEscopos.data.success) {
          aviso(respUsuarios.data.message || respEscopos.data.message || 'Erro ao carregar usuários e escopos');
        }
      } catch (error) {
        console.log(error);
        aviso('Erro ao carregar usuários e escopos — feche e tente novamente');
      }
    })();
  }, [aviso]);

  const toggleEscopo = (escopo) => {
    setEscopos((prev) => (prev.includes(escopo) ? prev.filter((e) => e !== escopo) : [...prev, escopo]));
  };

  const criar = async () => {
    if (!nome.trim()) return aviso('Nome é obrigatório');
    if (!usuarioId) return aviso('Selecione o usuário responsável');
    if (escopos.length === 0) return aviso('Selecione ao menos um escopo');
    setSalvando(true);
    try {
      const response = await apiUser.post('/apikeys/create', {
        nome: nome.trim(), usuario_id: usuarioId, escopos,
      });
      const { success, message, dados } = response.data;
      if (!success) return aviso(message);
      setChaveCriada(dados);
      onCriada();
    } catch (error) {
      console.log(error);
      aviso('Erro ao criar a chave');
    } finally {
      setSalvando(false);
    }
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(chaveCriada.chave);
      setCopiada(true);
    } catch (error) {
      console.log(error);
      aviso('Não foi possível copiar — selecione o texto manualmente');
    }
  };

  if (chaveCriada) {
    return (
      <Modal
        title="Chave criada"
        subtitle="Copie agora — ela não será exibida novamente"
        onClose={onClose}
        closeOnBackdrop={false}
        footer={<BaseButton onClick={onClose}>Fechar</BaseButton>}
      >
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Guarde a chave em um local seguro. O ERP armazena apenas o hash — se perdê-la, revogue e crie outra.
          </p>
          <code className="block w-full break-all rounded-lg bg-gray-100 dark:bg-gray-900 p-4 text-sm text-gray-900 dark:text-gray-100">
            {chaveCriada.chave}
          </code>
          <BaseButton variant="secondary" onClick={copiar}>
            {copiada ? 'Copiada!' : 'Copiar chave'}
          </BaseButton>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Gerar chave de API"
      subtitle="A chave nasce ativa e aparece uma única vez"
      onClose={onClose}
      footer={(
        <>
          <BaseButton variant="ghost" onClick={onClose}>Cancelar</BaseButton>
          <BaseButton onClick={criar} loading={salvando}>Gerar chave</BaseButton>
        </>
      )}
    >
      <div className="p-6 space-y-4">
        <BaseInput
          label="Nome (sistema que vai usar a chave)"
          name="nome"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder='Ex.: "Portal RH"'
        />
        <BaseSelect
          label="Usuário responsável"
          name="usuario_id"
          value={usuarioId}
          onChange={(e) => setUsuarioId(e.target.value)}
          options={usuarios.map((u) => ({ id: String(u.id), nome: `${u.name} (${u.email})` }))}
        />
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Escopos habilitados</div>
          <div className="grid grid-cols-2 gap-2">
            {escoposDisponiveis.map((escopo) => (
              <label key={escopo} className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200 cursor-pointer">
                <input type="checkbox" checked={escopos.includes(escopo)} onChange={() => toggleEscopo(escopo)} />
                <code>{escopo}</code>
              </label>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CriarChaveModal;
